import { RotaButton, RotaDropdownHeader } from "@teamrota/rota-design";
import styled, { keyframes } from "styled-components";
import { COLORS, STYLE_CONSTS } from "~/src/styles/config.style";

export const Container = styled.div`
  position: relative;
  height: auto;
  margin-top: ${({ isMarginTop }) => (!isMarginTop ? "35px" : 0)};
`;

export const Text = styled.div`
  color: ${({ theme }) => theme.text.primary};
  font-size: ${STYLE_CONSTS.FONT_MEDIUM};
  margin-top: ${({ marginTop }) => (marginTop ? STYLE_CONSTS.UNIT_2 : 0)};
  margin-bottom: ${({ marginBottom }) =>
    marginBottom ? STYLE_CONSTS.UNIT_2 : 0};
  text-transform: ${({ uppercase }) => (uppercase ? "uppercase" : "none")};
  border-bottom: ${({ borderBottom }) =>
    borderBottom ? STYLE_CONSTS.SMALL_DIVIDER_BORDER : "none"};
  padding-bottom: ${({ borderBottom }) =>
    borderBottom ? STYLE_CONSTS.UNIT_3 : 0};
  cursor: ${({ pointer }) => (pointer ? "pointer" : "default")};
  width: ${({ limitWidth }) => limitWidth && "max-content"};
`;

export const TableContainer = styled.div`
  width: 100%;
  margin: 1rem 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  max-height: 50vh;
`;
export const TableWrapper = styled.div`
  flex: 1 1 auto;
  position: relative;
`;

export const StyledTable = styled.table`
  min-width: 100%;
  width: 100%;
  table-layout: fixed;
  background: white;
  border: 1px solid #e5e7eb;
  border-collapse: collapse;
  overflow: visible;
`;

export const TableHead = styled.thead`
  background: ${COLORS.MAIN_ORANGE};
  position: sticky;
  top: -32px;
  z-index: 1;
`;

// Updated SortArrow component
export const SortArrow = styled.span`
  display: inline-block;
  width: 0;
  height: 0;
  margin-left: 6px;
  
  /* Up arrow */
  ${props =>
    props.direction === "ascending" &&
    `
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 5px solid ${COLORS.BLACK};
  `}
  
  /* Down arrow */
  ${props =>
    props.direction === "descending" &&
    `
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid ${COLORS.BLACK};
  `}
  
  /* Position absolute to stick to the right */
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
`;

// Updated TableHeader with relative positioning to anchor the absolute arrow
export const TableHeader = styled.th`
  padding: 0.75rem;
  text-align: left;
  font-weight: normal;
  color: ${COLORS.BLACK};
  width: ${props => props.width || "auto"};
  min-width: ${props => props.minWidth || "auto"};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &.sortable-header {
    cursor: pointer;
    user-select: none;
    position: relative; /* This is important for absolute positioning of arrow */
    transition: background-color 0.2s;
    padding-right: 24px; /* Add padding on right to make space for arrow */

    &:hover {
      background-color: rgba(255, 255, 255, 0.2);
    }
  }
`;

export const TableRow = styled.tr`
  ${props =>
    props.hasError &&
    `
    background-color: ${COLORS.BACKGROUND_RED}
  `}
`;

export const TableCell = styled.td`
  border-top: 1px solid #e5e7eb;
  width: ${props => props.width || "auto"};
  min-width: ${props => props.minWidth || "auto"};
  padding: 0 0.25rem;
`;

export const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 1rem;
`;

export const ActionButton = styled(RotaButton)`
  margin-left: 0.75rem;
`;

export const DropdownWrapper = styled.div`
  position: relative;

  /* Style for better text expansion on hover */
  & .dropdown-menu > li {
    &:hover {
      overflow: visible !important;
      white-space: normal !important;
      z-index: 1000;
      position: relative;
      background-color: white;
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
      border-radius: 4px;
    }
  }
`;

export const SearchWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
`;

export const StyledFilterWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 100%;
  margin-right: 10px;
`;

export const StyledDropdownsContainer = styled.div`
  border-radius: 4px;
  border: ${({ theme }) => `solid 1px ${theme.neutral.light}`};
  background-color: ${({ theme }) => theme.white};

  & > * {
    border-radius: 0;
  }
  & > :first-child {
    border-radius: 4px 4px 0 0;
  }
  & > :last-child {
    border-radius: 4px 4px 0 0;
  }
`;

export const StyledRotaDropdownHeader = styled(RotaDropdownHeader)`
  width: 100%;
  opacity: 0;

  animation: fade-in 0.3s forwards;

  @keyframes fade-in {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  & > div:first-of-type {
    border: none;
    padding: 9px 12px;
    width: 100%;
  }
`;

export const StyledSeparator = styled.div`
  width: 80%;
  margin: 0 auto;
  height: 1px;
  background-color: ${({ theme }) => theme.neutral.light};
`;

export const StyledChipsContainer = styled.div`
  width: 100%;
  max-width: 100%;
  margin-left: 20px;
  height: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: center;
`;

const pulseAnimation = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
`;

export const SkeletonItem = styled.div`
  height: 32px;
  width: 100%;
  border-radius: 4px;
  background: #f0f0f0;
  animation: ${pulseAnimation} 1.5s infinite ease-in-out;
  margin: 16px 0px;
`;
